import { useEffect, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Checkbox, FormControlLabel,
  Button, Alert, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import {
  getGuardDutyConfig, updateFeatures, updateGDRegions, disableGuardDuty,
  startGDRolesScan, getGDRolesJob, fixGDRoles,
} from '../../../services/guarddutyApi';
import RolePermissionsPanel from '../../../components/portal/securityhub/RolePermissionsPanel';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import MultiSelectField from './MultiSelectField';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, listCardSx, sectionCardSx, sectionHeadingSx, dialogProps, dialogTitleSx,
} from '../portalStyles';

// GuardDuty accent, shared with every other GuardDuty route.
const GD = '#E65100';
const GD_DARK = '#BF360C';

const primaryBtnSx = {
  backgroundColor: GD,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  px: 3,
  mt: 1,
  '&:hover': { backgroundColor: GD_DARK },
};

const textBtnSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  color: CK.body,
};

const dangerBtnSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  px: 3,
};

const tabSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.9rem',
  minHeight: 56,
  color: CK.body,
  '&.Mui-selected': { color: GD },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };

const checkboxSx = { color: CK.disabled, '&.Mui-checked': { color: GD } };

const featureLabelSx = {
  display: 'block',
  '& .MuiFormControlLabel-label': { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.navy },
};

const settingsHeader = (
  <PortalPageHeader
    icon={<SettingsIcon sx={{ fontSize: 24 }} />}
    title="Settings"
    subtitle="Manage protection plans and GuardDuty status"
    color={GD}
  />
);

// GuardDuty protection plans. The raw enum names are what the backend
// updateFeatures expects.
const FEATURE_OPTIONS = [
  { id: 'S3_DATA_EVENTS', label: 'S3 Protection (data events)' },
  { id: 'EKS_AUDIT_LOGS', label: 'EKS Protection (audit logs)' },
  { id: 'EBS_MALWARE_PROTECTION', label: 'Malware Protection (EBS)' },
  { id: 'RDS_LOGIN_EVENTS', label: 'RDS Protection (login events)' },
  { id: 'LAMBDA_NETWORK_LOGS', label: 'Lambda Protection (network logs)' },
  { id: 'RUNTIME_MONITORING', label: 'Runtime Monitoring' },
];

export default function GuardDutySettingsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [features, setFeatures] = useState([]);
  const [regions, setRegions] = useState([]);
  const [confirm, setConfirm] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const c = await getGuardDutyConfig();
        const cfg = c?.data;
        setConfig(cfg);
        if (cfg) {
          try { setFeatures(JSON.parse(cfg.enabledFeatures || '[]')); } catch (_) { setFeatures([]); }
          try { setRegions(JSON.parse(cfg.linkingRegions || '[]')); } catch (_) { setRegions([]); }
        }
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveFeatures = async () => {
    setBusy(true);
    try {
      await updateFeatures(features);
      setErr(null);
      // Hand off to the Dashboard entry — it polls config and renders the
      // in-progress banner until the update finishes.
      navigate('/securityhub/guardduty');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveRegions = async () => {
    if (!regions.includes(config.homeRegion)) {
      setErr('Home region cannot be removed');
      return;
    }
    setBusy(true);
    try {
      await updateGDRegions(regions);
      setErr(null);
      // Hand off to the Dashboard entry — it polls config and renders the
      // in-progress banner while regions are enrolled/torn down.
      navigate('/securityhub/guardduty');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const doDisable = async () => {
    setBusy(true);
    try {
      await disableGuardDuty();
      setConfirmOpen(false);
      setErr(null);
      // Hand off to the Dashboard entry — it polls config and renders the
      // in-progress banner during teardown, then the setup wizard once
      // status transitions to "disabled".
      navigate('/securityhub/guardduty');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Loading keeps the header mounted and swaps only the card body, so the page
  // does not jump once the settings land.
  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading settings…" color={GD} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert
            severity="info"
            sx={{ borderRadius: '8px', fontFamily: FONT_BODY, '& .MuiAlert-message': { fontFamily: FONT_BODY } }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/guardduty')} sx={{ ...textBtnSx, color: 'inherit' }}>
                Go to setup
              </Button>
            }
          >
            GuardDuty is not set up yet for this customer. Complete the setup wizard first to manage protection plans or disable the service.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
      {err && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={err} />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={listCardSx}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: `1px solid ${CK.border}`,
            '& .MuiTabs-indicator': { backgroundColor: GD, height: 3 },
          }}
        >
          <Tab label="Features" sx={tabSx} />
          <Tab label="Regions" sx={tabSx} />
          <Tab label="Permissions" sx={tabSx} />
          <Tab label="Danger zone" sx={tabSx} />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Protection plans</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              Toggle the GuardDuty protection plans enabled across every member
              account and region.
            </Typography>
            {FEATURE_OPTIONS.map((f) => (
              <FormControlLabel
                key={f.id}
                control={
                  <Checkbox
                    sx={checkboxSx}
                    checked={features.includes(f.id)}
                    onChange={(e) => setFeatures((cur) =>
                      e.target.checked ? [...cur, f.id] : cur.filter((x) => x !== f.id))}
                  />
                }
                label={f.label}
                sx={featureLabelSx}
              />
            ))}
            <Button
              variant="contained"
              disabled={busy}
              onClick={saveFeatures}
              sx={primaryBtnSx}
            >
              Save features
            </Button>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Linking regions</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              Home region: <b>{config.homeRegion}</b> (read-only)
            </Typography>
            <Typography sx={{ ...bodyTextSx, color: CK.muted, mb: 2 }}>
              Adding a region enrolls every member account there; removing a region
              tears down its detectors and prunes its findings.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <MultiSelectField
                label="Linking regions"
                options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
                value={regions}
                onChange={setRegions}
                lockedValues={config.homeRegion ? [config.homeRegion] : []}
              />
            </Box>
            <Button variant="contained" disabled={busy} onClick={saveRegions} sx={primaryBtnSx}>Save regions</Button>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <RolePermissionsPanel
              startScan={startGDRolesScan}
              getJob={getGDRolesJob}
              fix={fixGDRoles}
              capabilityLabel="GuardDuty"
            />
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Danger zone</Typography>
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
              Disabling tears down all member enrollments and GuardDuty detectors
              in every region. Finding history in Prism is retained.
            </Alert>
            <Button color="error" variant="contained" onClick={() => setConfirmOpen(true)} sx={dangerBtnSx}>
              Disable GuardDuty
            </Button>
          </Box>
        )}
      </Paper>
      </motion.div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Confirm disable</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1, wordBreak: 'break-word' }}>
            Type the customer ID <b>{config.customerId}</b> to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Customer ID"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={textBtnSx}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy || confirm !== config.customerId}
            onClick={doDisable}
            sx={dangerBtnSx}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
